import { useState, useRef, useEffect, useMemo } from 'react';
import { clampUiScale } from './GanttUtils';

// Custom hook encapsulating shared Gantt UI state, refs, and effects.
// Used by both GanttChart.js (project) and TemplateGantt.js (template).
const useGanttState = (storageKeyPrefix, links, subtasks, tasks) => {
    // Container refs
    const scrollContainerRef = useRef(null);
    const containerRef = useRef(null);
    const headerRef = useRef(null);

    // Dimensions
    const [containerWidth, setContainerWidth] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);

    // Scale and zoom - persist to localStorage
    const [currentScale, setCurrentScale] = useState(() => {
        return localStorage.getItem(`${storageKeyPrefix}Scale`) || 'day';
    });
    // Zoom is kept PER SCALE: day, week and month each remember their own level,
    // so switching away and back no longer comes back squashed. Same storage key
    // as before, now holding an object.
    const [zoomByScale, setZoomByScale] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}Zoom`);
        if (!saved) return {};
        // Migration: existing sessions hold a bare number, which is not valid
        // JSON. Keep it for whichever scale is current and let the others
        // auto-fit on first visit; without this the first load after deploy
        // silently resets everyone to 100%.
        const asNumber = parseFloat(saved);
        const currentKey = localStorage.getItem(`${storageKeyPrefix}Scale`) || 'day';
        try {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object') return parsed;
        } catch (err) {
            // falls through to the numeric migration below
        }
        return Number.isFinite(asNumber) ? { [currentKey]: asNumber } : {};
    });

    // ALWAYS a number, never the map - 25 references across the two charts read
    // this, several with no `|| 1` guard, and a map would fail silently there.
    const zoomLevel = zoomByScale[currentScale] ?? 1;
    // Whether this scale has ever been zoomed. Replaces the old isFirstLoad
    // boolean, which could not answer the question per scale.
    const hasZoomForScale = zoomByScale[currentScale] !== undefined;
    // True first load (nothing stored for ANY scale) still frames the whole
    // project; arriving at a new scale later fits the current window instead.
    const hasAnyZoom = Object.keys(zoomByScale).length > 0;
    const setZoomLevel = (value) => {
        setZoomByScale(prev => ({
            ...prev,
            [currentScale]: typeof value === 'function' ? value(prev[currentScale] ?? 1) : value
        }));
    };

    // READING SIZE - the second scale (T363, 2026-08-30).
    //
    // It owns a different axis from zoomLevel and NOTHING multiplies them: this
    // one drives row height, bar height and the type, and no width at all. See
    // GanttUtils.js for the whole rule.
    //
    // clampUiScale is the strict parse of a persisted UI value, not a Rule-4
    // fallback - the same shape as the boolean keys below and the numeric zoom
    // migration above. It deliberately does NOT snap: the wheel accumulates on a
    // raw value and only the applied one is snapped.
    const [uiScale, setUiScale] = useState(
        () => clampUiScale(parseFloat(localStorage.getItem(`${storageKeyPrefix}UiScale`)))
    );

    // ⛔ PERSISTENCE IS ON GESTURE END, NOT PER CHANGE - and that is why there is
    // no useEffect for it beside the others below.
    //
    // A pinch changes this value every frame. The house pattern here - a
    // useEffect on the value - would mean a synchronous localStorage write 60
    // times a second. setUiScale writes nothing; persistUiScale writes.
    const uiScaleRef = useRef(uiScale);
    uiScaleRef.current = uiScale;
    // ⚠️ TAKES AN EXPLICIT VALUE for the one caller that needs it. A gesture
    // ends after several renders, so the ref is current and the bare call is
    // right - but the V.Zoom reset persists in the SAME tick as its setUiScale,
    // when the ref still holds the pre-click value. That caller passes what it
    // just set. Not a default masking missing data: both forms are deliberate,
    // and the argument is the more specific of the two.
    const persistUiScale = (value) => {
        const next = value === undefined ? uiScaleRef.current : value;
        localStorage.setItem(`${storageKeyPrefix}UiScale`, String(next));
    };

    // ⛔ AND THE SAME ARGUMENT REACHES zoomLevel, WHICH DOES HAVE A PER-CHANGE
    // EFFECT (T379).
    //
    // shift+wheel and a horizontal pinch both drive setZoomLevel, and the effect
    // below writes on every change - carrying a JSON.stringify of the whole
    // per-scale map with it. That is the same defect, arriving by a second door.
    //
    // The effect STAYS: it is what makes a slider commit, a -/+ press and a FIT
    // durable, and none of those is continuous. It is merely suppressed while a
    // gesture is live, and persistZoom() runs once at the end.
    //
    // ⛔ persistZoom reads the REF, not closure state: a gesture-end handler
    // closes over its render's value, which on a fast final frame is one behind.
    // ⛔ AND NOTHING MAY WRITE STATE AFTER gestureActiveRef CLEARS - a
    // setZoomLevel issued after the clear has its effect fire with suppression
    // already off, which is the actual double-write. Persist, then clear, then
    // stop.
    const gestureActiveRef = useRef(false);
    const zoomByScaleRef = useRef(null);
    const persistZoom = () => {
        localStorage.setItem(`${storageKeyPrefix}Zoom`, JSON.stringify(zoomByScaleRef.current));
    };

    // Expanded state for hierarchical items (stored by ID)
    const [expandedItems, setExpandedItems] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}ExpandedItems`);
        return saved ? JSON.parse(saved) : {};
    });

    // Reorder drag state (for dragging subtasks to reorder)
    const [reorderDrag, setReorderDrag] = useState(null);

    // Drag state for task bars
    const [dragState, setDragState] = useState(null);
    const [dragPreview, setDragPreview] = useState(null);
    const [pushedTaskPreview, setPushedTaskPreview] = useState(null);
    const dragStateRef = useRef(null);
    const dragPreviewRef = useRef(null);
    const pushedTaskPreviewRef = useRef(null);

    // Position overrides - persists intended positions until props confirm
    const positionOverridesRef = useRef({});

    // Link overrides - persists link changes until props confirm
    const [pendingLinks, setPendingLinks] = useState({ added: [], removed: [] });

    // Editing state for date cells
    const [editingDateRow, setEditingDateRow] = useState(null);

    // Dependency creation state (shift+drag from subtask to subtask)
    const [dependencyDrag, setDependencyDrag] = useState(null);
    const dependencyDragRef = useRef(null);
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

    // Status change popup state
    const [statusPopup, setStatusPopup] = useState(null);

    // Assignee change popup state
    const [assigneePopup, setAssigneePopup] = useState(null);

    // Weekend work popup state
    const [weekendPopup, setWeekendPopup] = useState(null);

    // Dependency colour popup state
    const [colorPopup, setColorPopup] = useState(null);
    const [topLinkKey, setTopLinkKey] = useState(null);

    // Custom tooltip state
    const [tooltip, setTooltip] = useState(null);
    // ⛔ The hover card's PLACEMENT is not here any more (T403): it lives in
    // useAnchoredPlacement, which owns its own state and dies with its anchor.
    // `tooltip` - the card's content and its anchor point - is still shared
    // state, because both charts' hover logic reads and writes it.

    // Resizable Task Name column
    const [nameColWidth, setNameColWidth] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}NameColWidth`);
        return saved ? parseInt(saved, 10) : 220;
    });
    const [colResizing, setColResizing] = useState(null);

    // Collapsible columns (organise T162 port, 2026-08-21): strict === 'true'
    // parse - a boolean read, not a Rule-4 fallback.
    const [nameColCollapsed, setNameColCollapsed] = useState(() =>
        localStorage.getItem(`${storageKeyPrefix}NameColCollapsed`) === 'true');
    const [datesCollapsed, setDatesCollapsed] = useState(() =>
        localStorage.getItem(`${storageKeyPrefix}DatesCollapsed`) === 'true');

    // Flat views + lane filter (Bill's design, 2026-08-21): strict parses -
    // anything unrecognised is the grouped tree / All.
    const [viewMode, setViewMode] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}ViewMode`);
        return ['mt', 'mtst', 'st'].includes(saved) ? saved : null;
    });
    const [laneFilter, setLaneFilter] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}LaneFilter`);
        return ['tme', 'team'].includes(saved) ? saved : 'all';
    });

    // Time slider state (scroll position as percentage) - persist to localStorage
    const [scrollPercent, setScrollPercent] = useState(() => {
        const saved = localStorage.getItem(`${storageKeyPrefix}ScrollPercent`);
        return saved ? parseFloat(saved) : 0;
    });

    // Keep ref in sync with state
    useEffect(() => {
        dependencyDragRef.current = dependencyDrag;
    }, [dependencyDrag]);

    // Save expanded state to localStorage
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}ExpandedItems`, JSON.stringify(expandedItems));
    }, [expandedItems, storageKeyPrefix]);

    // Save column width to localStorage
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}NameColWidth`, nameColWidth.toString());
    }, [nameColWidth, storageKeyPrefix]);

    // Save collapsed columns to localStorage
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}NameColCollapsed`, String(nameColCollapsed));
    }, [nameColCollapsed, storageKeyPrefix]);
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}DatesCollapsed`, String(datesCollapsed));
    }, [datesCollapsed, storageKeyPrefix]);
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}ViewMode`, viewMode || '');
    }, [viewMode, storageKeyPrefix]);
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}LaneFilter`, laneFilter);
    }, [laneFilter, storageKeyPrefix]);

    // Save scale to localStorage
    useEffect(() => {
        localStorage.setItem(`${storageKeyPrefix}Scale`, currentScale);
    }, [currentScale, storageKeyPrefix]);

    // Save the whole per-scale map.
    //
    // ⛔ SUPPRESSED WHILE A GESTURE IS LIVE (T379). A pinch or a wheel gesture
    // drives this value every frame, and this line is a synchronous
    // localStorage write carrying a JSON.stringify of the whole map. The
    // gesture calls persistZoom() once when it ends instead.
    //
    // ⚠️ NOT rAF-coalesced, deliberately, unlike the scroll position below: a
    // gesture HAS an end event, so gesture-end is exact where rAF would be an
    // approximation. The two mechanisms are chosen per case.
    useEffect(() => {
        zoomByScaleRef.current = zoomByScale;
        if (gestureActiveRef.current) return;
        localStorage.setItem(`${storageKeyPrefix}Zoom`, JSON.stringify(zoomByScale));
    }, [zoomByScale, storageKeyPrefix]);

    // Column resize handlers.
    //
    // Pointer events since T370 (2026-08-30), so the column can be dragged by
    // finger like everything else on this chart.
    //
    // ⛔ THERE IS NO uiScale DIVISION HERE, AND THERE MUST NEVER BE ONE.
    // organise-app added one because ITS left panel was drawn scaled, then had
    // to take it out again when the panel stopped scaling - left in against an
    // unscaled panel it resizes the column at HALF SPEED at a reading size of 2.
    // TME's panel is never scaled (uiScale moves no width), so the division is
    // never right here in either direction.
    //
    // ⛔ The end path is idempotent: pointerup and lostpointercapture BOTH fire
    // on an ordinary release, so it runs twice every time.
    useEffect(() => {
        if (!colResizing) return;

        const handlePointerMove = (e) => {
            const deltaX = e.clientX - colResizing.startX;
            const newWidth = Math.max(120, Math.min(500, colResizing.startWidth + deltaX));
            setNameColWidth(newWidth);
        };

        const handlePointerUp = () => {
            setColResizing(prev => (prev === null ? prev : null));
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
        window.addEventListener('lostpointercapture', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
            window.removeEventListener('lostpointercapture', handlePointerUp);
        };
    }, [colResizing]);

    // Clear drag preview when data changes, but only if not actively dragging
    useEffect(() => {
        if (!dragStateRef.current) {
            dragPreviewRef.current = null;
            setDragPreview(null);
        }
    }, [tasks, subtasks]);

    // Clear position overrides when props confirm the expected values
    useEffect(() => {
        const overrides = positionOverridesRef.current;
        const keys = Object.keys(overrides);
        if (keys.length === 0) return;

        keys.forEach(key => {
            const override = overrides[key];
            // Key format: "projectId:taskId" or "default:taskId"
            const colonIdx = key.indexOf(':');
            const projectId = colonIdx > 0 ? key.substring(0, colonIdx) : null;
            const taskId = colonIdx > 0 ? key.substring(colonIdx + 1) : key;

            // Check if it's a subtask (match by taskId AND projectId)
            const subtask = subtasks.find(st =>
                String(st.taskId) === String(taskId) &&
                (projectId === 'default' || st.projectId === projectId)
            );
            if (subtask) {
                const propsStart = subtask.startDate || subtask.start || subtask.dueDate;
                const propsEnd = subtask.dueDate || subtask.end;
                if (propsStart === override.start && propsEnd === override.end) {
                    delete positionOverridesRef.current[key];
                }
                return;
            }
            // Check if it's a parent task (main task)
            const parentTask = tasks.find(t =>
                String(t.taskId) === String(taskId) &&
                (projectId === 'default' || t.projectId === projectId)
            );
            if (parentTask) {
                // Calculate effective dates from subtasks
                const taskSubtasks = subtasks.filter(st => String(st.mainTaskId) === String(taskId) && st.projectId === parentTask.projectId);
                if (taskSubtasks.length > 0) {
                    let minStart = null;
                    let maxEnd = null;
                    for (const st of taskSubtasks) {
                        const stStart = st.startDate || st.start || st.dueDate;
                        const stEnd = st.dueDate || st.end;
                        if (stStart) {
                            const startDate = new Date(stStart);
                            if (!minStart || startDate < minStart) minStart = startDate;
                        }
                        if (stEnd) {
                            const endDate = new Date(stEnd);
                            if (!maxEnd || endDate > maxEnd) maxEnd = endDate;
                        }
                    }
                    if (minStart && maxEnd) {
                        const effectiveStart = minStart.toISOString().split('T')[0];
                        const effectiveEnd = maxEnd.toISOString().split('T')[0];
                        if (effectiveStart === override.start && effectiveEnd === override.end) {
                            delete positionOverridesRef.current[key];
                        }
                    }
                }
            }
        });
    }, [subtasks, tasks]);

    // Clear pending link overrides when props confirm the changes
    useEffect(() => {
        let needsUpdate = false;
        let newAdded = pendingLinks.added;
        let newRemoved = pendingLinks.removed;

        // Clear added links that now exist in props
        if (pendingLinks.added.length > 0) {
            newAdded = pendingLinks.added.filter(addedLink => {
                const existsInProps = links.some(l =>
                    String(l.source) === String(addedLink.source) &&
                    String(l.target) === String(addedLink.target)
                );
                return !existsInProps;
            });
            if (newAdded.length !== pendingLinks.added.length) needsUpdate = true;
        }

        // Clear removed links that no longer exist in props
        if (pendingLinks.removed.length > 0) {
            newRemoved = pendingLinks.removed.filter(removedLink => {
                const stillInProps = links.some(l =>
                    String(l.source) === String(removedLink.source) &&
                    String(l.target) === String(removedLink.target)
                );
                return stillInProps;
            });
            if (newRemoved.length !== pendingLinks.removed.length) needsUpdate = true;
        }

        if (needsUpdate) {
            setPendingLinks({ added: newAdded, removed: newRemoved });
        }
    }, [links, pendingLinks]);

    // Compute effective links (props + pending added - pending removed)
    const effectiveLinks = useMemo(() => {
        let result = links.filter(l => {
            const isRemoved = pendingLinks.removed.some(r =>
                String(r.source) === String(l.source) &&
                String(r.target) === String(l.target)
            );
            return !isRemoved;
        });

        for (const addedLink of pendingLinks.added) {
            const alreadyExists = result.some(l =>
                String(l.source) === String(addedLink.source) &&
                String(l.target) === String(addedLink.target)
            );
            if (!alreadyExists) {
                result.push(addedLink);
            }
        }

        return result;
    }, [links, pendingLinks]);

    // Measure container width.
    //
    // A ResizeObserver, NOT just window.resize (T380, Bill 2026-08-30). Full
    // screen is CSS, not the Fullscreen API - the PAGE swaps its wrapper from
    // `h-full` to `fixed inset-0` (Timeline.js:313, ClientView.js) - so entering
    // it changes this container's width and fires NO window resize. The old
    // measurement therefore kept the smaller pre-full-screen value until
    // something else resized the window, and two things read it:
    //
    //   - the column-build memo, whose deps include containerWidth
    //     (GanttChart.js:1157), so `columns`, `pixelsPerDay` and `columnWidth`
    //     were never rebuilt for the new width;
    //   - minTimelineWidth (GanttChart.js:1939), so at low zoom the timeline
    //     stayed narrower than the viewport it should fill.
    //
    // Intermittent rather than constant, because rotating a tablet DOES fire a
    // resize and hides it - which is what made it read as a rendering glitch.
    //
    // window.resize is kept as well: an observer answers for the element, and
    // the listener costs nothing next to it.
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        window.addEventListener('resize', updateWidth);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    // Measure header height for accurate arrow positioning
    useEffect(() => {
        const updateHeaderHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        if (headerRef.current) {
            observer.observe(headerRef.current);
        }
        return () => observer.disconnect();
    }, [currentScale]);

    // Ref to track saved scroll position for restoration after data changes
    const savedScrollRef = useRef(localStorage.getItem(`${storageKeyPrefix}ScrollPercent`));

    // Sync scroll position with slider and persist to localStorage
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        // ⛔ THE localStorage WRITE IS rAF-COALESCED (T368, 2026-08-30).
        //
        // This fired a synchronous write on EVERY scroll event. A finger pan
        // scrolls every frame, so that is a write per frame - the same defect
        // the zoom persistence above avoids, and the reason a pan felt heavy.
        //
        // ⚠️ rAF here and gesture-end there, deliberately: a scroll has no end
        // event, it simply stops. The last position would be lost without the
        // FLUSH in the cleanup below.
        let pending = null;
        let frame = null;
        const flush = () => {
            frame = null;
            if (pending === null) return;
            localStorage.setItem(`${storageKeyPrefix}ScrollPercent`, pending);
            savedScrollRef.current = pending;
            pending = null;
        };

        const handleScroll = () => {
            const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
            if (maxScroll > 0) {
                const newPercent = (scrollContainer.scrollLeft / maxScroll) * 100;
                setScrollPercent(newPercent);
                pending = newPercent.toString();
                if (frame === null) frame = requestAnimationFrame(flush);
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            if (frame !== null) cancelAnimationFrame(frame);
            // Flush AFTER cancelling, or the position the user left the chart
            // at is dropped on unmount and on every scale switch.
            flush();
        };
    }, [storageKeyPrefix]);

    // Restore scroll position when data changes
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        const savedPercent = savedScrollRef.current;
        if (!scrollContainer || !savedPercent || !tasks || !subtasks) return;

        requestAnimationFrame(() => {
            const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
            if (maxScroll > 0) {
                scrollContainer.scrollLeft = (parseFloat(savedPercent) / 100) * maxScroll;
            }
        });
    }, [tasks, subtasks]);

    return {
        // Container refs
        scrollContainerRef,
        containerRef,
        headerRef,

        // Dimensions
        containerWidth,
        headerHeight,

        // Scale and zoom
        currentScale, setCurrentScale,
        zoomLevel, setZoomLevel, hasZoomForScale, hasAnyZoom,

        // Reading size - the second scale, and the gesture-end persistence both
        // scales share. gestureActiveRef is what suppresses the per-change zoom
        // write while a pinch or a wheel gesture is running.
        uiScale, setUiScale, persistUiScale,
        persistZoom, gestureActiveRef,

        // Expanded items
        expandedItems, setExpandedItems,

        // Reorder drag
        reorderDrag, setReorderDrag,

        // Task drag state + refs
        dragState, setDragState,
        dragPreview, setDragPreview,
        pushedTaskPreview, setPushedTaskPreview,
        dragStateRef, dragPreviewRef, pushedTaskPreviewRef,

        // Position overrides
        positionOverridesRef,

        // Pending links
        pendingLinks, setPendingLinks,

        // Date cell editing
        editingDateRow, setEditingDateRow,

        // Dependency drag + cursor
        dependencyDrag, setDependencyDrag,
        cursorPos, setCursorPos,
        dependencyDragRef,

        // Popups
        statusPopup, setStatusPopup,
        assigneePopup, setAssigneePopup,
        weekendPopup, setWeekendPopup,
        colorPopup, setColorPopup,
        topLinkKey, setTopLinkKey,

        // Tooltip
        tooltip, setTooltip,

        // Column resize
        nameColWidth, setNameColWidth,
        colResizing, setColResizing,

        // Collapsible columns
        nameColCollapsed, setNameColCollapsed,
        datesCollapsed, setDatesCollapsed,

        // Flat views + lane filter
        viewMode, setViewMode,
        laneFilter, setLaneFilter,

        // Scroll
        scrollPercent, setScrollPercent,

        // Computed
        effectiveLinks
    };
};

export { useGanttState };
